// src/DeviceState.js
import React, { useState } from "react"; // Import useState
import logger from "./utils/logger";
import { useSectionCollapse } from "./hooks/useSectionCollapse";
import "./DeviceState.css";

const labelMapping = {
  deviceId: "ID",
  displayName: "Display Name",
  batteryLevel: "Battery Level",
  batteryPercentage: "Battery Percentage",
  serialNumber: "Serial Number",
  mechanicalVariance: "Variance",
  model: "Model",
  daysInUse: "Days In Use",
  doorPosition: "Door Position",
  firmwareVersion: "Firmware Version",
  lockState: "Lock State",
  lockdownState: "Lockdown State",
  lockMode: "Lock Mode",
  deadboltState: "Deadbolt State",
  ipbState: "Internal Push Button",
  totalMotorCycles: "Total Motor Cycles",
  powerSource: "Power Source",
  noTour: "No Tour State",
  connectivityStatus: "Wi-Fi Connectivity Status",
  // LockV1 boolean state flags (read-only)
  isPassage: "Is Passage",
  isSecured: "Is Secured",
  isPrivacySecured: "Is Privacy Secured",
  isMomentaryUnlocked: "Is Momentary Unlocked",
  isFrozenNormal: "Is Frozen Normal",
  isFrozenPassage: "Is Frozen Passage",
  isFrozenSecured: "Is Frozen Secured",
  isHolidayRestricted: "Is Holiday Restricted",
  // BLE diagnostics (read-only)
  bleApplicationVersion: "BLE App Version",
  bleBootloaderVersion: "BLE Bootloader Version",
  bleSecureElementVersion: "BLE Secure Element Version",
  // Diagnostic (read-only)
  wakeUpCount: "Wake Up Count",
  // Storage capacity (read-only)
  remainingCredentials: "Remaining Credentials",
  remainingCredentialSchedules: "Remaining Credential Schedules",
  remainingDeviceSchedules: "Remaining Device Schedules",
  remainingHolidaySchedules: "Remaining Holiday Schedules",
  lanIpAddress: "LAN IP Address",
  macAddress: "MAC Address",
  activeSsid: "Wi-Fi SSID",
  activeSignalStrength: "Signal Strength",
  activeAccessPointType: "Access Point Type",
  activeEncryptionAlgorithm: "WiFi Encryption",
  activeEnterpriseUsername: "Enterprise Username",
  wifiAppVersion: "WiFi App Version",
  wifiBootVersion: "WiFi Boot Version",
  firmwareInstalledAt: "Firmware Installed",
  // No Tour (read-only diagnostics)
  noTourState: "No Tour State",
  noTourDoorId: "No Tour Door ID",
  noTourGroupIds: "No Tour Group IDs",
  noTourActiveSector: "No Tour Active Sector",
  noTourRemainingCredentials: "No Tour Remaining Credentials",
  noTourSha256HashSiteKey: "No Tour Site Key Hash",
};


// --- Helper rendering functions ---
const renderConnectivityStatus = (statusText) => {
  if (!statusText) return String(statusText);
  const lowerCaseStatus = String(statusText).toUpperCase();
  if (lowerCaseStatus === "ONLINE") {
    return <span className="status-online">ONLINE</span>;
  }
  if (lowerCaseStatus === "OFFLINE") {
    return <span className="status-offline">OFFLINE</span>;
  }
  return String(statusText);
};

const renderLockStateValue = (value) => {
  if (!value) return String(value);
  const stringValue = String(value);
  const upperCaseValue = stringValue.toUpperCase();

  // Handle frozen lock states
  if (upperCaseValue === "FROZEN_SECURED") {
    return <span className="state-frozen-secured">FROZEN_SECURED</span>;
  }
  if (upperCaseValue === "FROZEN_PASSAGE") {
    return <span className="state-frozen-passage">FROZEN_PASSAGE</span>;
  }

  // Handle normal lock states
  const lowerCaseValue = stringValue.toLowerCase();
  if (lowerCaseValue === "passage") {
    return <span className="state-passage">{value}</span>;
  }
  if (lowerCaseValue === "secured") {
    return <span className="state-secured">{value}</span>;
  }
  return String(value);
};

const renderLockdownStateValue = (value) => {
  if (!value) return String(value);
  const stringValue = String(value);

  // Handle lockdown states
  if (stringValue === "Lockdown On") {
    return <span className="state-lockdown-on">Lockdown On</span>;
  }
  if (stringValue === "Lockdown Off") {
    return <span className="state-lockdown-off">Lockdown Off</span>;
  }
  return String(value);
};

const renderBatteryLevel = (levelText) => {
  if (!levelText) return String(levelText);
  const upperCaseLevel = String(levelText).toUpperCase();

  let className = "";
  switch (upperCaseLevel) {
    case "NORMAL":
      className = "battery-normal";
      break;
    case "LOW":
      className = "battery-low";
      break;
    case "CRITICAL":
      className = "battery-critical";
      break;
    default:
      break;
  }
  return <span className={className}>{levelText}</span>;
};

const renderBatteryPercentage = (percentage) => {
  const numPercentage = Number(percentage);
  if (isNaN(numPercentage)) {
    return String(percentage);
  }

  let className = "";
  if (numPercentage >= 40) {
    className = "battery-normal";
  } else if (numPercentage >= 10) {
    className = "battery-low";
  } else {
    className = "battery-critical";
  }
  return <span className={className}>{numPercentage}%</span>;
};

const renderDeadboltState = (value) => {
  if (!value) return String(value);
  const stringValue = String(value);
  const upperCaseValue = stringValue.toUpperCase();

  logger.log(
    `[DeviceState] Rendering deadboltState: "${stringValue}" (uppercase: "${upperCaseValue}")`
  );

  if (upperCaseValue === "SECURED") {
    return <span className="deadbolt-secured">Secured</span>;
  }
  if (upperCaseValue === "PASSAGE") {
    return <span className="deadbolt-passage">Passage</span>;
  }
  if (upperCaseValue === "UNKNOWN") {
    return <span className="deadbolt-other">Unknown</span>;
  }
  // Any other value gets orange styling
  return <span className="deadbolt-other">{stringValue}</span>;
};

const renderDoorPosition = (value) => {
  if (!value) return String(value);
  const upperCaseValue = String(value).toUpperCase();

  if (upperCaseValue === "OPEN") {
    return <span className="door-position-open">OPEN</span>;
  }
  if (upperCaseValue === "SHUT") {
    return <span className="door-position-shut">SHUT</span>;
  }
  return String(value);
};
// --- End Helper Functions ---

const DeviceState = ({
  deviceState,
  deviceId,
  isCollapsed,
  onToggleCollapse,
  dragHandleProps,
  onEditDisplayNameClick,
  onEditCredentialsSettingClick,
  connectivityStatusUpdateTime,
}) => {
  // --- START: ADDED CODE ---
  // State to manage the "Copied!" feedback for deviceId
  const [isIdCopied, setIsIdCopied] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const { isSectionCollapsed, toggleSection } = useSectionCollapse();
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Reusable copy function
  const handleCopy = (text, type, event) => {
    navigator.clipboard.writeText(text).then(
      () => {
        if (type === "deviceId") {
          setIsIdCopied(true);
          setTimeout(() => setIsIdCopied(false), 2000); // Reset after 2 seconds

          // Show tooltip at click position
          if (event) {
            setTooltipPosition({ x: event.clientX, y: event.clientY });
            setShowTooltip(true);
            setTimeout(() => setShowTooltip(false), 1000); // Hide after 1 second
          }
        }
      },
      (err) => {
        logger.error("Failed to copy: ", err);
        // You could show an error notification here
      }
    );
  };
  // --- END: ADDED CODE ---

  if (!deviceState) {
    return null;
  }

  // Priority keys always appear first with no section header
  const priorityKeys = ["displayName", "deviceId", "serialNumber", "model"];

  // Ordered groups — each group renders a section header followed by its fields
  // in the defined sequence. Any labelMapping key not listed here falls into "Other".
  const FIELD_GROUPS = [
    {
      label: "Lock Status",
      keys: [
        "lockState", "deadboltState", "ipbState", "doorPosition",
        "lockMode", "lockdownState",
        "isPassage", "isSecured", "isPrivacySecured", "isMomentaryUnlocked",
        "isFrozenNormal", "isFrozenPassage", "isFrozenSecured", "isHolidayRestricted",
      ],
    },
    {
      label: "Connectivity",
      keys: [
        "connectivityStatus", "activeSsid", "activeSignalStrength",
        "activeAccessPointType", "activeEncryptionAlgorithm", "activeEnterpriseUsername",
        "lanIpAddress", "macAddress",
        "wifiAppVersion", "wifiBootVersion",
      ],
    },
    {
      label: "Firmware & Power",
      keys: [
        "firmwareVersion", "firmwareInstalledAt",
        "batteryLevel", "batteryPercentage", "powerSource",
      ],
    },
    {
      label: "Device Info",
      keys: ["daysInUse", "mechanicalVariance", "totalMotorCycles", "wakeUpCount"],
    },
    {
      label: "BLE",
      keys: ["bleApplicationVersion", "bleBootloaderVersion", "bleSecureElementVersion"],
    },
    {
      label: "Capacity",
      keys: [
        "remainingCredentials", "remainingCredentialSchedules",
        "remainingDeviceSchedules", "remainingHolidaySchedules",
      ],
    },
    {
      label: "No Tour",
      keys: [
        "noTour", "noTourState",
        "noTourDoorId", "noTourGroupIds", "noTourEncryptedSiteKey",
        "noTourActiveSector", "noTourRemainingCredentials", "noTourSha256HashSiteKey",
      ],
    },
  ];

  const groupedKeys = new Set(FIELD_GROUPS.flatMap((g) => g.keys));
  const otherKeys = Object.keys(labelMapping).filter(
    (k) => !priorityKeys.includes(k) && !groupedKeys.has(k)
  );
  if (otherKeys.length > 0) {
    FIELD_GROUPS.push({ label: "Other", keys: otherKeys });
  }

  const initialKeys = [];
  for (const key of priorityKeys) {
    if (key === "deviceId" && deviceId) {
      initialKeys.push("deviceId");
    } else if (key !== "deviceId" && (deviceState[key] !== undefined && deviceState[key] !== null)) {
      initialKeys.push(key);
    }
  }

  return (
    <div className="device-state-container">
      <h2 onClick={onToggleCollapse} {...dragHandleProps}>
        Device State
        {deviceState.serialNumber ? `: ${deviceState.serialNumber}` : ""}
        <span className="collapse-icon">{isCollapsed ? "▼" : "▲"}</span>
      </h2>

      {!isCollapsed && (
        <div className="state-grid">
          {/* Priority fields — no section header */}
          {initialKeys.map((key) => {
            const value = key === "deviceId" ? deviceId : deviceState[key];
            return (
              <React.Fragment key={key}>
                <div className="state-item">
                  <span className="state-label">
                    {labelMapping[key] || key}:
                    {key === "connectivityStatus" &&
                      connectivityStatusUpdateTime && (
                        <div className="last-update-time">
                          (Last Update:{" "}
                          {new Date(
                            connectivityStatusUpdateTime
                          ).toLocaleString()}
                          )
                        </div>
                      )}
                  </span>
                  <span className="state-value">
                    {value !== undefined && value !== null ? (
                      key === "connectivityStatus" ? (
                        renderConnectivityStatus(value)
                      ) : key === "lockState" ? (
                        renderLockStateValue(value)
                      ) : key === "lockdownState" ? (
                        renderLockdownStateValue(value)
                      ) : key === "batteryLevel" ? (
                        renderBatteryLevel(value)
                      ) : key === "batteryPercentage" ? (
                        renderBatteryPercentage(value)
                      ) : key === "deadboltState" ? (
                        renderDeadboltState(value)
                      ) : key === "doorPosition" ? (
                        renderDoorPosition(value)
                      ) : key === "deviceId" ? (
                        <span
                          className="device-id-text"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(value, "deviceId", e);
                          }}
                          title="Click to copy Device ID"
                          style={{ cursor: "pointer" }}
                        >
                          {String(value)}
                        </span>
                      ) : (
                        String(value)
                      )
                    ) : (
                      <span className="state-not-sampled">Not Yet Sampled</span>
                    )}
                    {key === "displayName" && (
                      <button
                        className="edit-display-name-button"
                        onClick={onEditDisplayNameClick}
                        title="Edit Display Name"
                        aria-label="Edit Display Name"
                      >
                        ✏️
                      </button>
                    )}
                    {/* --- START: ADDED COPY BUTTON --- */}
                    {key === "deviceId" && value && (
                      <button
                        className="copy-icon-button"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent collapse on click
                          handleCopy(value, "deviceId", e);
                        }}
                        title="Copy Device ID"
                        aria-label="Copy Device ID"
                      >
                        {isIdCopied ? "✅" : "📋"}
                      </button>
                    )}
                    {/* --- END: ADDED COPY BUTTON --- */}
                  </span>
                </div>
              </React.Fragment>
            );
          })}

          {/* Expanded sections — render full header + content in defined order */}
          {FIELD_GROUPS.map((group) => {
            const visibleKeys = group.keys.filter((key) => labelMapping[key]);
            if (visibleKeys.length === 0 || isSectionCollapsed(group.label)) return null;
            return (
              <React.Fragment key={group.label}>
                <div
                  className="device-state-section-header device-state-section-header--clickable"
                  onClick={() => toggleSection(group.label)}
                >
                  <span>{group.label}</span>
                  <span className="section-collapse-icon">▼</span>
                </div>
                {visibleKeys.map((key) => {
                  const value = deviceState[key];
                  return (
                    <div className="state-item" key={key}>
                      <span className="state-label">
                        {labelMapping[key] || key}:
                        {key === "connectivityStatus" && connectivityStatusUpdateTime && (
                          <div className="last-update-time">
                            (Last Update: {new Date(connectivityStatusUpdateTime).toLocaleString()})
                          </div>
                        )}
                      </span>
                      <span className="state-value">
                        {value !== undefined && value !== null ? (
                          key === "connectivityStatus" ? renderConnectivityStatus(value)
                          : key === "lockState" ? renderLockStateValue(value)
                          : key === "lockdownState" ? renderLockdownStateValue(value)
                          : key === "batteryLevel" ? renderBatteryLevel(value)
                          : key === "batteryPercentage" ? renderBatteryPercentage(value)
                          : key === "deadboltState" ? renderDeadboltState(value)
                          : key === "doorPosition" ? renderDoorPosition(value)
                          : String(value)
                        ) : (
                          <span className="state-not-sampled">Not Yet Sampled</span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}

          {/* Collapsed sections — rendered as chips in a single compact row */}
          {(() => {
            const chips = FIELD_GROUPS.filter((g) => {
              const visibleKeys = g.keys.filter((key) => labelMapping[key]);
              return visibleKeys.length > 0 && isSectionCollapsed(g.label);
            });
            if (chips.length === 0) return null;
            return (
              <div className="device-state-chips-bar">
                {chips.map((group) => (
                  <button
                    key={group.label}
                    className="device-state-section-chip"
                    onClick={() => toggleSection(group.label)}
                    title={`Expand ${group.label}`}
                  >
                    {group.label} <span className="chip-expand-icon">▶</span>
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Tooltip for ID copied */}
      {showTooltip && (
        <div
          className="id-copied-tooltip"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y - 40}px`,
          }}
        >
          ID Copied
        </div>
      )}
    </div>
  );
};

export default DeviceState;
